<script lang="ts">
	import { onMount } from 'svelte';
	import { VpIcon } from '@vtmn-play/svelte/VpIcon';
	import { _ } from '../../utility-services/Translation.service';

	let indicatorElement: HTMLDivElement | undefined;
	let isScrollable = $state(false);
	let isDragging = $state(false);
	let thumbHeight = $state(0);
	let thumbOffset = $state(0);
	let scrollPercentage = $state(0);
	type ScrollBehaviorOption = 'auto' | 'instant' | 'smooth';
	const scrollUpLabel = $derived($_('scroll_up'));
	const scrollDownLabel = $derived($_('scroll_down'));
	const scrollPositionLabel = $derived($_('scroll_position'));
	let dragOffset = 0;

	function clamp(value: number, minimum: number, maximum: number) {
		return Math.min(Math.max(value, minimum), maximum);
	}

	function getScrollMetrics() {
		const scrollElement = document.scrollingElement ?? document.documentElement;
		const maxScroll = Math.max(scrollElement.scrollHeight - window.innerHeight, 0);
		const trackHeight = indicatorElement?.clientHeight ?? 0;
		const availableTrack = Math.max(trackHeight - thumbHeight, 0);

		return { scrollElement, maxScroll, availableTrack };
	}

	function scrollPage(direction: -1 | 1) {
		const { scrollElement, maxScroll } = getScrollMetrics();
		const scrollDistance = Math.max(window.innerHeight * 0.35, 120);
		const targetScrollTop = Math.min(
			Math.max(scrollElement.scrollTop + direction * scrollDistance, 0),
			maxScroll
		);

		window.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
	}

	function scrollToTrackPosition(
		clientY: number,
		behavior: ScrollBehaviorOption = 'smooth',
		pointerOffset = thumbHeight / 2
	) {
		if (!indicatorElement) return;

		const { maxScroll, availableTrack } = getScrollMetrics();
		if (maxScroll <= 0 || availableTrack <= 0) return;

		const trackTop = indicatorElement.getBoundingClientRect().top;
		const nextThumbOffset = clamp(clientY - trackTop - pointerOffset, 0, availableTrack);
		const scrollRatio = nextThumbOffset / availableTrack;

		window.scrollTo({ top: scrollRatio * maxScroll, behavior });
	}

	function handleTrackPointerDown(event: PointerEvent) {
		if (!isScrollable || event.target !== event.currentTarget) return;

		scrollToTrackPosition(event.clientY);
	}

	function handleThumbPointerDown(event: PointerEvent) {
		if (!isScrollable) return;

		event.preventDefault();
		event.stopPropagation();
		const thumbElement = event.currentTarget as HTMLElement;
		dragOffset = event.clientY - thumbElement.getBoundingClientRect().top;
		isDragging = true;
		window.addEventListener('pointermove', handleThumbPointerMove);
		window.addEventListener('pointerup', stopDragging);
		window.addEventListener('pointercancel', stopDragging);
	}

	function handleThumbPointerMove(event: PointerEvent) {
		if (!isDragging) return;

		scrollToTrackPosition(event.clientY, 'auto', dragOffset);
	}

	function stopDragging() {
		isDragging = false;
		window.removeEventListener('pointermove', handleThumbPointerMove);
		window.removeEventListener('pointerup', stopDragging);
		window.removeEventListener('pointercancel', stopDragging);
	}

	function handleKeyDown(event: KeyboardEvent) {
		if (event.key === 'ArrowUp' || event.key === 'PageUp') {
			event.preventDefault();
			scrollPage(-1);
		} else if (event.key === 'ArrowDown' || event.key === 'PageDown') {
			event.preventDefault();
			scrollPage(1);
		} else if (event.key === 'Home' || event.key === 'End') {
			event.preventDefault();
			const { maxScroll } = getScrollMetrics();
			window.scrollTo({
				top: event.key === 'Home' ? 0 : maxScroll,
				behavior: 'smooth'
			});
		}
	}

	onMount(() => {
		const updateIndicator = () => {
			if (!indicatorElement) return;

			const scrollElement = document.scrollingElement ?? document.documentElement;
			const viewportHeight = window.innerHeight;
			const maxScroll = Math.max(scrollElement.scrollHeight - viewportHeight, 0);
			const trackHeight = indicatorElement.clientHeight;

			if (maxScroll <= 0 || trackHeight <= 0) {
				isScrollable = false;
				thumbOffset = 0;
				scrollPercentage = 0;
				return;
			}

			const visibleRatio = Math.min(viewportHeight / scrollElement.scrollHeight, 1);
			const nextThumbHeight = Math.max(trackHeight * visibleRatio, 32);
			const availableTrack = Math.max(trackHeight - nextThumbHeight, 0);
			const scrollRatio = Math.min(Math.max(scrollElement.scrollTop / maxScroll, 0), 1);

			isScrollable = true;
			thumbHeight = nextThumbHeight;
			thumbOffset = availableTrack * scrollRatio;
			scrollPercentage = Math.round(scrollRatio * 100);
		};

		window.addEventListener('scroll', updateIndicator, { passive: true });
		window.addEventListener('resize', updateIndicator);

		const resizeObserver =
			typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updateIndicator);
		resizeObserver?.observe(document.documentElement);
		resizeObserver?.observe(document.body);
		updateIndicator();

		return () => {
			stopDragging();
			window.removeEventListener('scroll', updateIndicator);
			window.removeEventListener('resize', updateIndicator);
			resizeObserver?.disconnect();
		};
	});
</script>

<div
	bind:this={indicatorElement}
	class="scroll-indicator"
	class:is-visible={isScrollable}
	class:is-dragging={isDragging}
	role="scrollbar"
	aria-label={scrollPositionLabel}
	aria-controls="page-content"
	aria-orientation="vertical"
	aria-valuemin="0"
	aria-valuemax="100"
	aria-valuenow={scrollPercentage}
	tabindex="0"
	onkeydown={handleKeyDown}
	onpointerdown={handleTrackPointerDown}
	data-testid="scroll-indicator"
>
	<button
		type="button"
		class="scroll-indicator__button scroll-indicator__button--up"
		aria-label={scrollUpLabel}
		onclick={() => scrollPage(-1)}
		data-testid="scroll-indicator-up"
	>
		<VpIcon
			class="scroll-indicator__arrow"
			name="chevron-up"
			size={24}
			stroke-width={3}
			aria-hidden="true"
		/>
	</button>
	<div
		class="scroll-indicator__thumb"
		role="presentation"
		style={`height: ${thumbHeight}px; transform: translateY(${thumbOffset}px);`}
		onpointerdown={handleThumbPointerDown}
		data-testid="scroll-indicator-thumb"
	></div>
	<button
		type="button"
		class="scroll-indicator__button scroll-indicator__button--down"
		aria-label={scrollDownLabel}
		onclick={() => scrollPage(1)}
		data-testid="scroll-indicator-down"
	>
		<VpIcon
			class="scroll-indicator__arrow"
			name="chevron-down"
			size={24}
			stroke-width={3}
			aria-hidden="true"
		/>
	</button>
</div>

<style>
	.scroll-indicator {
		position: fixed;
		top: 50%;
		right: var(--vtmn-spacing_4, 1rem);
		z-index: 1000;
		width: 0.5rem;
		height: min(75vh, 35rem);
		background: var(--vp-semantic-color-border-quiet);
		border-radius: 999px;
		opacity: 0;
		overflow: visible;
		pointer-events: none;
		transform: translateY(-50%);
		transition: opacity 180ms ease;
	}

	.scroll-indicator.is-visible {
		opacity: 0.85;
		pointer-events: auto;
		cursor: pointer;
	}

	.scroll-indicator__thumb {
		position: absolute;
		inset-inline: 0;
		top: 0;
		min-height: 2.5rem;
		background: var(--vtmn-semantic-color_background-brand-primary);
		border-radius: inherit;
		cursor: grab;
		touch-action: none;
	}

	.scroll-indicator.is-dragging .scroll-indicator__thumb {
		cursor: grabbing;
	}

	:global(.scroll-indicator__button) {
		position: absolute;
		left: 50%;
		z-index: 1;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 2.5rem;
		height: 2.5rem;
		padding: 0;
		border: 0;
		border-radius: 999px;
		background: transparent;
		color: var(--vtmn-semantic-color_content-active);
		cursor: pointer;
		pointer-events: auto;
		transform: translateX(-50%);
	}

	:global(.scroll-indicator__button:hover) {
		background: var(--vp-semantic-color-container-quiet);
	}

	:global(.scroll-indicator__button:focus-visible) {
		outline: 2px solid var(--vtmn-semantic-color_content-active);
		outline-offset: 2px;
	}

	:global(.scroll-indicator__button--up) {
		bottom: calc(100% + var(--vtmn-spacing_2, 0.5rem));
	}

	:global(.scroll-indicator__button--down) {
		top: calc(100% + var(--vtmn-spacing_2, 0.5rem));
	}

	:global(.scroll-indicator__arrow) {
		display: block;
		color: inherit;
	}

	@media screen and (max-width: 768px) {
		.scroll-indicator {
			right: var(--vtmn-spacing_2, 0.5rem);
			width: 0.375rem;
			height: min(42vh, 18rem);
		}
		:global(.scroll-indicator__button) {
			width: 2.25rem;
			height: 2.25rem;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.scroll-indicator {
			transition: none;
		}
	}
</style>
